import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, Input, OnChanges, OnDestroy, PLATFORM_ID, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ChartType } from 'chart.js';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { getPreSelection } from '../pre-selection';
import { Indicator } from '../models/indicator';
import { arraySet } from '../../../utils/arrays';
import { unsubscribe } from 'src/app/utils/helpers';
import { Subscription } from 'rxjs';
import { IndicatorsService } from '../indicators.service';
import { IndicatorValue } from '../models/indicator-value';

type RequestState = 'loading' | 'ready' | 'error';

function getArrayQueryParam(queryParamMap: ParamMap, key: string): string[] | undefined {
    const values = queryParamMap.getAll(key).filter((value) => !!value);
    return values.length ? values : undefined;
}

@Component({
  standalone: false,
  selector: 'app-indicator-values-component',
  templateUrl: './indicators-values.component.html',
  styleUrls: ['./indicators-values.component.scss'],
})
export class IndicatorsValuesComponent implements OnChanges, OnDestroy {
    private readonly subscriptions: Subscription[] = [];
    private currentLoad?: Subscription;
    private loadId = 0;
    private readonly isBrowser: boolean;

    @Input() indicator: Indicator;

    readonly icons = {
        loader: faSpinner,
    };
    indicatorValues: IndicatorValue[] | null = null;
    requestState: RequestState = 'ready';
    error: unknown;

    subjects: string[] = [];
    selectedSubjects: string[] = [];
    periods: string[] = [];
    selectedPeriods: string[] = [];
    chartTypes: { label: string; value: ChartType }[] = [
        { label: 'filters.chartType.values.bar', value: 'bar' },
        { label: 'filters.chartType.values.line', value: 'line' },
    ];
    chartType: ChartType = this.chartTypes[0].value;
    chartTypeDirty = false;

    constructor(
        private readonly indicatorsService: IndicatorsService,
        private readonly route: ActivatedRoute,
        private readonly cdr: ChangeDetectorRef,
        @Inject(PLATFORM_ID) platformId: object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
        unsubscribe(this.subscriptions);
    }

    ngOnChanges(_: SimpleChanges): void {
        this.getData();
    }

    private getData() {
        const loadId = ++this.loadId;
        this.currentLoad?.unsubscribe();
        this.error = undefined;
        this.indicatorValues = null;
        this.subjects = [];
        this.periods = [];
        this.selectedSubjects = [];
        this.selectedPeriods = [];

        if (!this.indicator || !this.isBrowser) {
            this.indicatorValues = [];
            this.requestState = 'ready';
            this.cdr.markForCheck();
            return;
        }

        this.requestState = 'loading';
        this.cdr.markForCheck();
        this.currentLoad = this.indicatorsService
            .getIndicatorValues(this.indicator.key)
            .subscribe({
                next: (rows: IndicatorValue[]) => {
                    if (loadId !== this.loadId) {
                        return;
                    }

                    this.indicatorValues = rows;
                    this.subjects = arraySet(rows.map((value) => value.subject));
                    this.periods = arraySet(rows.map((value) => value.period));
                    this.preSelect();
                    this.requestState = 'ready';
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    if (loadId !== this.loadId) {
                        return;
                    }

                    this.error = err;
                    this.indicatorValues = [];
                    this.subjects = [];
                    this.periods = [];
                    this.requestState = 'error';
                    this.cdr.markForCheck();
                    console.error('[Indicators] indicator values request failed', err);
                },
            });

        this.subscriptions.push(this.currentLoad);
    }
    private preSelect() {
        const preSelection = getPreSelection(this.indicator.group, this.indicator.short, this.indicator.breakdown) || { subjects: [] };
        const queryParamMap = this.route.snapshot.queryParamMap;
        const subjectParams = getArrayQueryParam(queryParamMap, 'subject');
        const periodParams = getArrayQueryParam(queryParamMap, 'period');
        const chartTypeParam = queryParamMap.get('chartType') as ChartType | null;

        if (this.subjects.length <= 20) {
            this.selectedSubjects = subjectParams || [...this.subjects];
        } else {
            this.selectedSubjects = preSelection.subjects.length ? [...preSelection.subjects] : [...this.subjects.slice(0, 20)];
        }

        if (periodParams?.length) {
            this.selectedPeriods = periodParams.filter((period) => this.periods.includes(period));
        } else if (this.subjects.length > 1 && this.periods.length > 1) {
            this.selectedPeriods = [this.periods.slice().sort()[this.periods.length - 1]];
        } else {
            this.selectedPeriods = [...this.periods];
        }

        if (!this.selectedPeriods.length && this.periods.length) {
            this.selectedPeriods = [...this.periods];
        }

        if (chartTypeParam && this.chartTypes.some((option) => option.value === chartTypeParam)) {
            this.chartType = chartTypeParam;
        } else if (this.subjects.length > 1 && this.periods.length > 1) {
            this.chartType = 'bar';
        } else if (this.subjects.length === 1 && this.periods.length > 1) {
            this.chartType = 'line';
        } else {
            this.chartType = this.selectedPeriods.length >= 3 ? 'line' : 'bar';
        }
    }

    onChartTypeChange() {
        this.chartTypeDirty = true;
    }

    ngOnDestroy(): void {
        this.currentLoad?.unsubscribe();
        unsubscribe(this.subscriptions);
    }
}
